import React, { Suspense, lazy } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-medium-image-zoom/dist/styles.css';
import './App.scss';

import Header from './components/Mainpage/Header';
import Footer from './components/Footer/Footer';
import AnnouncementBar from './components/Mainpage/AnnouncementBar';
import WhatsAppIcon from './components/WhatsApp/WhatsAppIcon';
import ScrollToTop from './components/Content/ScrollToTop';
import Loader from './components/Loader/Loader';
import ErrorBoundary from './components/UI/ErrorBoundary';
import NotFound from './components/UI/NotFound';
import Landingpage from './components/Landingpage/Landingpage';
import useSessionExpiry from './hooks/useSessionExpiry';

// ------------------------------------------------------------
//  Route-level code splitting.
//  Checkout and the profile editor pull in `country-state-city`
//  and `react-select`, which together dominated the main bundle
//  even for visitors who never reach those screens.
// ------------------------------------------------------------
const Home = lazy(() => import('./components/Content/Home'));
const CollectionRoute = lazy(() => import('./components/Content/CollectionRoute'));
const CategoryList = lazy(() => import('./components/Content/CategoryList'));
const Productshow = lazy(() => import('./components/Content/Productshow'));
const Showallreviews = lazy(() => import('./components/Content/Showallreviews'));
const AllBestReviews = lazy(() => import('./components/Content/AllBestReviews'));
const AboutUs = lazy(() => import('./components/Content/AboutUs'));
const TAC = lazy(() => import('./components/Content/TAC'));
const Policy = lazy(() => import('./components/Content/Policy'));
const Policies = lazy(() => import('./components/Content/Policies'));
const Search = lazy(() => import('./components/Search/Search'));
const Wishlist = lazy(() => import('./components/Wishlist/Wishlist'));
const Check = lazy(() => import('./components/Private_routes/Check'));
const CheckOut = lazy(() => import('./components/CheckOut/CheckOut'));
const CheckOutHeader = lazy(() => import('./components/CheckOut/CheckOutHeader'));
const Profile = lazy(() => import('./components/Content/Profile'));
const YourOrders = lazy(() => import('./components/Content/Your_Orders'));
const Order = lazy(() => import('./components/Content/Order'));

function App() {
  const location = useLocation();

  // Logs the user out with an alert as soon as the backend rejects a
  // request from them (401/403/419).
  useSessionExpiry();

  // The checkout route is `/check/checkoutpage`; matching on the
  // shared `/check/checkout` prefix keeps both spellings working.
  const isCheckOutPage = location.pathname.startsWith('/check/checkout');

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <AnnouncementBar />
      <Header />

      <ScrollToTop />

      <main className="app-main" id="main-content">
        <ErrorBoundary>
          <Suspense fallback={<Loader />}>
            {isCheckOutPage && <CheckOutHeader />}
            <Routes>
              <Route path="/" element={<Landingpage />} />
              <Route path="/wishlist" element={<Wishlist />} />

              <Route path="/check/*" element={<Check />}>
                <Route path="checkoutpage" element={<CheckOut />} />
                <Route path="profile" element={<Profile />} />
                <Route path="profile/your-orders" element={<YourOrders />} />
                <Route path="profile/your-orders/order/:order_id" element={<Order />} />
              </Route>

              <Route path="/all-best-reviews" element={<AllBestReviews />} />
              <Route path="/tac" element={<TAC />} />
              <Route path="/aboutus" element={<AboutUs />} />
              <Route path="/policy" element={<Policy />} />
              <Route path="/policies" element={<Policies />} />

              <Route path="/productshow/:product_code" element={<Productshow />} />
              {/* Legacy alias kept so previously shared links keep resolving. */}
              <Route path="/productshowlike/:product_code" element={<Productshow />} />

              <Route path="/allproducts" element={<Home />} />
              <Route path="/category" element={<Home />} />
              <Route path="/product" element={<Home />} />
              <Route path="/all-categories" element={<CategoryList />} />
              {/* Standalone versions of the landing-page collections. */}
              <Route path="/hot-products" element={<CollectionRoute collection="hot" />} />
              <Route path="/new-products" element={<CollectionRoute collection="new" />} />
              <Route path="/also-like" element={<CollectionRoute collection="recommended" />} />
              <Route path="/best-reviews" element={<CollectionRoute collection="reviews" />} />
              <Route path="/reviewshow/:product_code" element={<Showallreviews />} />
              <Route path="/search/:query" element={<Search />} />

              {/* Anything else lands on a real 404 instead of a blank page. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>

      {!isCheckOutPage && <Footer />}
      <WhatsAppIcon />
    </div>
  );
}

export default App;
